import { useRef, useState } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import { composerMorph, heroRise } from "./chatMotion";
import { authClient } from "../../lib/auth-client";
import { hapticSelection } from "../../lib/haptics";
import { ChatComposer } from "./ChatComposer";
import { ChatCapabilitiesSheet } from "./ChatCapabilitiesSheet";
import { ChatPresets } from "./ChatPresets";
import { pickPresets } from "./presetPrompts";

/** Display serif for the hero greeting only — loaded in public/index.html. */
export const SERIF_DISPLAY =
  '"Source Serif 4", Georgia, "Times New Roman", serif';

interface ChatHeroProps {
  /** Called with the user's first prompt — creates the conversation and sends it. */
  onStart: (prompt: string) => void;
  /** Conversation is being created — composer locks and the send button spins. */
  pending: boolean;
  /** Free-tier lock: everything renders but inputs are disabled with the upgrade tooltip. */
  locked?: boolean;
  /** Pre-fill the composer (e.g. "Tell me about Nebius Group N.V." from a ticker page). */
  initialValue?: string;
}

export const daypart = (): string => {
  const hour = new Date().getHours();
  if (hour < 12) return "morning";
  if (hour < 18) return "afternoon";
  return "evening";
};

/**
 * The /chat landing state — one serif greeting, one composer, and a short list
 * of starter prompts that show what the agent can actually do.
 *
 * The starters send IMMEDIATELY rather than pre-filling the composer. A
 * pre-filled input asks the user to read it, approve it and find the send
 * button before anything happens; a one-tap answer is the whole demonstration,
 * and the composer keeps working for anyone who wants to edit instead.
 */
export const ChatHero = ({
  onStart,
  pending,
  locked = false,
  initialValue,
}: ChatHeroProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  // Mobile autofocus pops the keyboard and scrolls the composer toward centre,
  // pushing the hero below the fold — desktop only.
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  // The hero logo is a HEIGHT question, not a platform one, so it is keyed to
  // the breakpoint alone (Ben, 2026-07-31). Only xs and sm (< 900px, i.e.
  // phones) are tight enough that a 48px lockup lands right under the top
  // bar's and reads as a repeat; from md up there is enough vertical room for
  // it to belong to the greeting instead. That deliberately includes native
  // iPads, which sit at md or above.
  const showHeroLogo = useMediaQuery(theme.breakpoints.up("md"));
  const [value, setValue] = useState(initialValue ?? "");
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [capabilitiesOpen, setCapabilitiesOpen] = useState(false);

  // Drawn once per mount, so the set is stable while typing but fresh every
  // time the user comes back to the landing.
  const [presets] = useState(() => pickPresets());

  const { data: session } = authClient.useSession();

  const firstName = session?.user?.name?.trim().split(/\s+/)[0];
  const greeting = firstName
    ? `Good ${daypart()}, ${firstName}`
    : `Good ${daypart()}`;

  const canSubmit = value.trim().length > 0 && !pending && !locked;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onStart(value.trim());
  };

  // A preset is just a first message — identical to typing it and pressing
  // enter, which is why none of this needs the API to know presets exist.
  const handlePreset = (prompt: string) => {
    if (pending || locked) return;
    onStart(prompt);
  };

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/*
       * Three rows, `1fr auto 1fr`, NOT a centred flex column.
       *
       * The starter cards must not drag the composer upward: /research
       * (ChartsPage) centres an identical logo + greeting + composer block in
       * the same 680px measure, and the two landings are meant to line up when
       * you switch tabs. In a plain centred column, anything added below is
       * part of what gets centred, so the composer rises by half the cards'
       * height and the tabs no longer match.
       *
       * Equal `1fr` rows above and below pin the middle row to the true centre
       * regardless of what row three holds. The cards hang from the top of row
       * three, close enough to the composer to read as belonging to it, and the
       * unused space collects below them. On a viewport too short to hold them,
       * row three grows past its share and the page scrolls — the composer
       * drifts up only in the case where it had to anyway.
       */}
      <Box
        sx={{
          minHeight: "100%",
          display: "grid",
          gridTemplateRows: "1fr auto 1fr",
          width: "100%",
          maxWidth: 680,
          mx: "auto",
          px: { xs: 2, md: 3 },
          py: { xs: 4, md: 6 },
        }}
      >
        <Box />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            width: "100%",
          }}
        >
          {/* md and up, matching /research exactly (same asset, same 48px,
              same 24px gap) so the two landings line up when you switch
              tabs. */}
          {showHeroLogo && (
            <Box sx={{ mb: 3, ...heroRise(0) }}>
              <img
                src={
                  isDark
                    ? "/insightLongLogoPrimaryWhiteText.svg"
                    : "/insightLongLogoPrimary.svg"
                }
                alt="Insight Analytics"
                style={{ height: 48, width: "auto", maxWidth: "100%" }}
              />
            </Box>
          )}

          <Typography
            component="h1"
            sx={{
              fontFamily: SERIF_DISPLAY,
              fontWeight: 500,
              fontSize: { xs: "1.75rem", md: "2.25rem" },
              letterSpacing: "-0.01em",
              lineHeight: 1.25,
              color: isDark ? "#ffffff" : "#1d1d1f",
              mb: { xs: 3.5, md: 4.5 },
              ...heroRise(0.05),
            }}
          >
            {greeting}
          </Typography>

          {/* No CSS entrance on the morph element itself — a keyframe transform
            would fight the framer-motion layout transform mid-glide. */}
          <motion.div
            layoutId="chat-composer"
            transition={composerMorph}
            style={{ width: "100%" }}
          >
            <ChatComposer
              value={value}
              onChange={setValue}
              onSend={handleSubmit}
              pending={pending}
              locked={locked}
              autoFocus={!isMobile}
              inputRef={inputRef}
              placeholder="How can I help with your research?"
            />
          </motion.div>
        </Box>

        {/* Row three. The cards hang from the TOP of the lower half, held off
            the composer by `pt` alone.
            Pinning them to the foot of the page was tried and rejected (Ben,
            2026-07-31): on a tall screen the gap grew until they read as
            unrelated to the input they belong to. Anchoring them near the
            composer keeps that relationship at any height, and lets the
            leftover space collect at the bottom where it costs nothing. */}
        <Box
          sx={{
            alignSelf: "start",
            width: "100%",
            pt: { xs: 5, md: 7 },
          }}
        >
          <ChatPresets
            presets={presets}
            disabled={locked || pending}
            onAsk={handlePreset}
            onExplain={() => {
              hapticSelection();
              setCapabilitiesOpen(true);
            }}
          />
        </Box>
      </Box>

      <ChatCapabilitiesSheet
        open={capabilitiesOpen}
        onClose={() => setCapabilitiesOpen(false)}
        onAsk={handlePreset}
      />
    </Box>
  );
};
